import { formatPrice } from '@/lib/format';
import {
  formatTravelEsimData,
  formatTravelEsimDataBullet,
  formatTravelEsimDuration,
  formatTravelEsimPricePerDay,
} from '@/lib/travel-esim';
import type { TravelEsimQuote } from '@octave/contracts';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Check, Plane } from 'lucide-react';

const shortDate = (iso: string) => format(parseISO(iso), 'd MMM', { locale: fr });
const longDate = (iso: string) =>
  format(parseISO(iso), 'd MMM yyyy', { locale: fr });

/** The live summary: empty prompt, spinner, no offer, or the priced stay. */
export function QuotePanel({
  destination,
  ready,
  loading,
  quote,
  onOrder,
}: {
  destination: string | undefined;
  ready: boolean;
  loading: boolean;
  quote: TravelEsimQuote | null | undefined;
  onOrder: () => void;
}) {
  if (!ready) {
    return (
      <PanelShell>
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <Plane className="h-5 w-5" />
          </span>
          <p className="text-sm font-medium text-slate-900">
            {destination ? `Votre eSIM pour ${destination}` : 'Votre eSIM internationale'}
          </p>
          <p className="text-sm text-slate-500">
            {destination
              ? 'Choisissez vos dates de séjour pour voir le prix.'
              : 'Choisissez une destination et vos dates de séjour pour voir le prix.'}
          </p>
        </div>
      </PanelShell>
    );
  }

  if (loading) {
    return (
      <PanelShell>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      </PanelShell>
    );
  }

  if (!quote) {
    return (
      <PanelShell>
        <div className="py-10 text-center">
          <p className="text-sm font-medium text-slate-900">
            Pas d'eSIM pour {destination}
          </p>
          <p className="text-sm text-slate-500">
            Cette destination n'est pas couverte pour le moment.
          </p>
        </div>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <div className="space-y-5">
        <div className="rounded-xl bg-emerald-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">
            Votre eSIM
          </p>
          <p className="text-lg font-semibold text-slate-900">{quote.country}</p>
          <p className="text-sm text-slate-600">
            du {shortDate(quote.departureDate)} au{' '}
            {longDate(quote.returnDate)} ·{' '}
            {formatTravelEsimDuration(quote.stayDurationDays)}
          </p>
        </div>

        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-2xl font-bold text-slate-900">
              {formatTravelEsimData(quote.dataLimitGb)}
            </p>
            <p className="text-xs text-slate-500">
              pendant {formatTravelEsimDuration(quote.stayDurationDays)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              {formatPrice(quote.totalPriceCents)}
            </p>
            <p className="text-xs text-slate-500">HT pour le séjour</p>
            <p className="text-sm font-medium text-emerald-700">
              soit {formatTravelEsimPricePerDay(quote.pricePerDayCents)}
            </p>
          </div>
        </div>

        <ul className="space-y-1.5 border-t border-slate-100 pt-4 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            {formatTravelEsimDataBullet(quote.dataLimitGb)}
          </li>
          <li className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            Activation immédiate par QR code, sans engagement
          </li>
        </ul>

        <button
          type="button"
          onClick={onOrder}
          className="w-full rounded-full bg-emerald-600 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md cursor-pointer"
        >
          Commander cette eSIM
        </button>
      </div>
    </PanelShell>
  );
}

function PanelShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm p-6">{children}</div>
  );
}

